import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import MobileMenu from '../MobileMenu';
import MobileHeader from '../MobileHeader';
import Footer from '../Footer';
import FloatingMenu from '../FloatingMenu';
import SEO from '../SEO';
import NewsletterSignup from '../NewsletterSignup';
import apiClient from '../../api/apiClient';
import {
  getAccessToken,
  getRefreshToken,
  removeTokens,
  setAccessToken,
} from '../../api/authUtils';
import { jwtDecode } from 'jwt-decode';
import '../styles/Header.css';
import './styles/BlogHome.css';

const BlogHome = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tags, setTags] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Fetch degli articoli dal backend
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        let url = '/api/blog/articles/search/';
        
        // Se c'è un tag selezionato, aggiungi il parametro di ricerca
        if (selectedTag) {
          url += `?tag=${selectedTag}`;
        }
        
        const response = await apiClient.get(url);
        setArticles(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Errore durante il recupero degli articoli:', err);
        setError('Impossibile caricare gli articoli. Riprova più tardi.');
        setLoading(false);
      }
    };

    fetchArticles();
  }, [selectedTag]);

  // Fetch dei tag disponibili
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await apiClient.get('/api/blog/tags/');
        setTags(response.data);
      } catch (err) {
        console.error('Errore durante il recupero dei tag:', err);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken) {
        try {
          const decodedAccessToken = jwtDecode(accessToken);
          const currentTime = Date.now() / 1000;

          if (decodedAccessToken.exp && decodedAccessToken.exp > currentTime) {
            setIsLoggedIn(true);
          } else if (refreshToken) {
            try {
              const response = await apiClient.post('/accounts/token/refresh/', {
                refresh: refreshToken,
              });

              if (response.status === 200) {
                const data = response.data;
                setAccessToken(data.access);
                setIsLoggedIn(true);
              } else {
                removeTokens();
                setIsLoggedIn(false);
              }
            } catch (error) {
              console.error('Errore durante il refresh del token:', error);
              removeTokens();
              setIsLoggedIn(false);
            }
          } else {
            removeTokens();
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Errore durante la decodifica del token di accesso:', error);
          removeTokens();
          setIsLoggedIn(false);
        }
      } else if (refreshToken) {
        try {
          const response = await apiClient.post('/accounts/token/refresh/', {
            refresh: refreshToken,
          });

          if (response.status === 200) {
            const data = response.data;
            setAccessToken(data.access);
            setIsLoggedIn(true);
          } else {
            removeTokens();
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Errore durante il refresh del token:', error);
          removeTokens();
          setIsLoggedIn(false);
        }
      } else {
        setIsLoggedIn(false);
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowFloatingMenu(true);
      } else {
        setShowFloatingMenu(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  // Formatta la data in formato italiano
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    if (isMobile) {
      // Formato compatto per mobile: dd/mm/yyyy
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } else {
      // Formato esteso per desktop
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString('it-IT', options);
    }
  };

  // Gestisce il click sul bottone di filtro
  const handleTagFilter = (tagSlug) => {
    setSelectedTag(tagSlug === 'tutto' ? null : tagSlug);
  };

  // Tronca la descrizione se è troppo lunga
  const truncateDescription = (text, maxLength = 100) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="blogpage">
      <SEO 
        title="Il Pensatoio di Minerva"
        description="Il Pensatoio di Minerva: blog di articoli su intelligenza artificiale, educazione e innovazione didattica."
        keywords="blog educazione, AI educativa, didattica innovativa, intelligenza artificiale scuola, insegnamento digitale"
        ogType="blog"
        ogUrl="https://minervaeducationai.it/blog"
        canonical="https://minervaeducationai.it/blog"
      />
    
      <Header
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Header mobile */}
      <MobileHeader 
        isLoggedIn={isLoggedIn} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />

      {mobileMenuOpen && (
        <MobileMenu
          isLoggedIn={isLoggedIn}
          setIsLoggedIn={setIsLoggedIn}
          setMobileMenuOpen={setMobileMenuOpen}
        />
      )}

      {showFloatingMenu && (
        <FloatingMenu
          isLoggedIn={isLoggedIn}
          handleLogout={() => {
            removeTokens();
            setIsLoggedIn(false);
          }}
        />
      )}

      <div className="container">
        <main>
          <motion.section
            className="blog-container"
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
          >
            <div className="blog-header">
              <h1>Il Pensatoio di Minerva</h1>
              <div className="blog-header-underline"></div>
            </div>

            {/* Filtri per tag */}
            <div className="blog-filters">
              <button 
                className={`filter-button ${!selectedTag ? 'active' : ''}`}
                onClick={() => handleTagFilter('tutto')}
              >
                Tutto
              </button>
              <button 
                className={`filter-button ${selectedTag === 'educazione-e-strumenti' ? 'active' : ''}`}
                onClick={() => handleTagFilter('educazione-e-strumenti')}
              >
                Educazione e Strumenti
              </button>
              <button 
                className={`filter-button ${selectedTag === 'ispirazione-per-insegnanti' ? 'active' : ''}`}
                onClick={() => handleTagFilter('ispirazione-per-insegnanti')}
              >
                Ispirazione per Insegnanti
              </button>
              <button 
                className={`filter-button ${selectedTag === 'attualita-e-novita' ? 'active' : ''}`}
                onClick={() => handleTagFilter('attualita-e-novita')}
              >
                Attualità e Novità
              </button>
            </div>

            {loading ? (
              <div className="blog-loading">
                <p>Caricamento articoli...</p>
              </div>
            ) : error ? (
              <div className="blog-error">
                <p>{error}</p>
              </div>
            ) : (
              <div className="articles-grid">
                {articles.map((article) => (
                  <Link 
                    to={`/blog/${article.slug}`} 
                    key={article.id} 
                    className="article-card"
                  >
                    <div className="article-image-container">
                      {article.cover_image ? (
                        <img 
                          src={article.cover_image} 
                          alt={article.title} 
                          className="article-image" 
                        />
                      ) : (
                        <div className="article-image-placeholder">
                          <img 
                            src="/logo/logogrey.svg" 
                            alt="Minerva Logo" 
                            className="placeholder-logo" 
                          />
                        </div>
                      )}
                    </div>
                    <div className="article-info">
                      <h2 className="blog-card-title">{article.title}</h2>
                      <div className="article-meta">
                        <span className="article-date">{formatDate(article.publication_date)}</span>
                        {article.author && (
                          <>
                            <span className="article-meta-separator">•</span>
                            <div className="article-author">{article.author}</div>
                          </>
                        )}
                      </div>
                      
                      {/* Tags dell'articolo */}
                      {article.tags && article.tags.length > 0 && (
                        <div className="article-tags">
                          {article.tags.map(tag => (
                            <span 
                              key={tag.id} 
                              className="article-tag" 
                              onClick={(e) => {
                                e.preventDefault();
                                handleTagFilter(tag.slug);
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                          {article.subtags && article.subtags.length > 0 && 
                            article.subtags.map(subtag => (
                              <span 
                                key={subtag.id} 
                                className="article-subtag"
                                onClick={(e) => {
                                  e.preventDefault();
                                  // Implementazione futura per filtrare per subtag
                                }}
                              >
                                {subtag.name}
                              </span>
                            ))
                          }
                        </div>
                      )}
                      
                      {article.meta_description && (
                        <p className="article-description">
                          {truncateDescription(article.meta_description, 120)}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </motion.section>
        </main>

        {/* Newsletter Section */}
        <motion.div
          className="newsletter-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <div className="newsletter-container">
            <h3>Resta aggiornato sul mondo dell'educazione</h3>
            <p>Iscriviti alla nostra newsletter per ricevere le ultime novità su intelligenza artificiale, strumenti didattici e consigli per insegnanti.</p>
            
            <div className="newsletter-form">
              <NewsletterSignup />
            </div>
          </div>
        </motion.div>
      </div>

      {/*<Footer />*/}
    </div>
  );
};

export default BlogHome; 